if (!customElements.get('m-cart-recommendations')) {
  class MCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.addProduct.bind(this);
      this.onNavigate = this.navigateRecommendations.bind(this);
      this.onScroll = this.updateProgress.bind(this);
    }

    connectedCallback() {
      this.list = this.querySelector('.m-cart-recommendations__list');
      this.previousButton = this.querySelector('[data-recommendations-prev]');
      this.nextButton = this.querySelector('[data-recommendations-next]');
      this.progress = this.querySelector('[data-recommendations-progress]');
      this.progressBar = this.querySelector('[data-recommendations-progress-bar]');
      this.addEventListener('click', this.onClick);
      this.previousButton?.addEventListener('click', this.onNavigate);
      this.nextButton?.addEventListener('click', this.onNavigate);
      this.list?.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      // The drawer can be hidden during initialization and cart section refreshes.
      if (this.list && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleProgressUpdate());
        this.resizeObserver.observe(this.list);
      }
      queueMicrotask(() => {
        if (!this.isConnected) return;
        if (this.dataset.mode === 'fixed') {
          this.updateVisibility();
        } else {
          this.load();
        }
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
      this.removeEventListener('click', this.onClick);
      this.previousButton?.removeEventListener('click', this.onNavigate);
      this.nextButton?.removeEventListener('click', this.onNavigate);
      this.list?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    updateVisibility() {
      const productCount = this.querySelectorAll('[data-recommendation-product]').length;
      const controls = this.querySelector('.m-cart-recommendations__controls');
      const progress = this.progress || this.querySelector('[data-recommendations-progress]');
      this.hidden = productCount === 0;
      if (controls) controls.hidden = this.dataset.layout !== 'carousel' || productCount < 2;
      if (progress) progress.hidden = this.dataset.layout !== 'carousel' || productCount < 2;
      this.updateProgress();
      this.scheduleProgressUpdate();
    }

    scheduleProgressUpdate() {
      if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
      this.progressFrame = requestAnimationFrame(() => {
        this.progressFrame = null;
        this.updateProgress();
      });
    }

    updateProgress() {
      if (!this.list || !this.progressBar) return;
      const viewportWidth = this.list.clientWidth;
      const totalWidth = this.list.scrollWidth;
      if (!viewportWidth || !totalWidth) {
        // Carousel cards each occupy one page; initialize before the drawer opens.
        const productCount = this.list.querySelectorAll('[data-recommendation-product]').length;
        this.progressBar.style.width = productCount ? `${100 / productCount}%` : '0%';
        this.progressBar.style.left = '0%';
        return;
      }

      const visibleRatio = Math.min(viewportWidth / totalWidth, 1);
      const maxScroll = Math.max(totalWidth - viewportWidth, 0);
      const scrollPosition = Math.min(Math.abs(this.list.scrollLeft), maxScroll);
      const scrollRatio = maxScroll ? scrollPosition / maxScroll : 0;
      const round = (value) => Math.round(value * 1000) / 1000;

      this.progressBar.style.width = `${round(visibleRatio * 100)}%`;
      this.progressBar.style.left = `${round((1 - visibleRatio) * scrollRatio * 100)}%`;
    }

    navigateRecommendations(event) {
      if (!this.list) return;
      const direction = event.currentTarget.dataset.recommendationsPrev !== undefined ? -1 : 1;
      this.list.scrollBy({ left: direction * this.list.clientWidth, behavior: 'smooth' });
    }

    async load() {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const ids = JSON.parse(this.dataset.productIds || '[]').map(String);
      const seen = new Set(ids);
      const limit = Number(this.dataset.limit) || 4;
      const list = this.list || this.querySelector('.m-cart-recommendations__list');
      if (!list) return;
      const cards = [];

      // Each freshly rendered cart component uses the current cart product IDs.
      for (const id of ids) {
        if (cards.length >= limit || controller.signal.aborted || !this.isConnected) break;
        try {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('product_id', id);
          url.searchParams.set('section_id', 'cart-recommendations');
          url.searchParams.set('limit', '10');
          url.searchParams.set('intent', 'related');
          const response = await fetch(url.toString(), { signal: controller.signal });
          if (!response.ok) continue;
          const html = await response.text();
          if (controller.signal.aborted || !this.isConnected) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          for (const card of doc.querySelectorAll('[data-recommendation-product]')) {
            const productId = card.dataset.recommendationProduct;
            if (seen.has(productId)) continue;
            seen.add(productId);
            cards.push(card);
            if (cards.length >= limit) break;
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          // A missing recommendation for one item must not block other items.
        }
      }
      if (controller.signal.aborted || !this.isConnected) return;
      list.append(...cards);
      this.updateVisibility();
    }

    async addProduct(event) {
      const button = event.target.closest('[data-recommendation-add]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      if (this.adding) return;
      this.adding = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const errorBox = this.querySelector('.m-cart-recommendations__error');
      errorBox.hidden = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: button.dataset.recommendationAdd, quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
        }
        // Reuse Minimog's cart, totals and cart-count section refresh.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (error) {
        if (this.isConnected) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
        }
      } finally {
        this.adding = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('m-cart-recommendations', MCartRecommendations);
}
